import React, { useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  FaHome, 
  FaCalendarAlt, 
  FaChartBar, 
  FaUsers, 
  FaMoon, 
  FaSun, 
  FaSignOutAlt, 
  FaGift,
  FaBars,
  FaTimes,
  FaCrown,
  FaUserShield
} from 'react-icons/fa';
import FestivalManagement from '../components/admin/FestivalManagement';
import WishesManagement from '../components/admin/WishesManagement';
import Reports from '../components/admin/Reports';
import UserManagement from '../components/superadmin/UserManagement';
import SpecialDaysManagement from '../components/admin/SpecialDaysManagement';
// Using Tailwind CSS classes instead of external CSS

const AdminPage = () => {
  // const { t } = useTranslation(); // Temporarily commented out
  const { user, logout, isSuperAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!user) {
    return <Navigate to="/imcloginforadmin" />;
  }

  const getCurrentPageTitle = () => {
    switch (location.pathname) {
      case '/admin/festivals':
        return 'Festival & Media Management';
      case '/admin/events':
        return 'Events & Wishes';
      case '/admin/special-days':
        return 'Special Days';
      case '/admin/reports':
        return 'Reports & Analytics';
      case '/admin/users':
        return 'User Management';
      default:
        return 'Admin Dashboard';
    }
  };

  const menuItems = [
    {
      path: '/admin/festivals',
      icon: FaCalendarAlt,
      label: 'Festivals & Media',
      description: 'Manage festivals and media content'
    },
    {
      path: '/admin/special-days',
      icon: FaCalendarAlt,
      label: 'Special Days',
      description: 'Manage special days for calendar'
    },
    {
      path: '/admin/events',
      icon: FaGift,
      label: 'Events & Wishes',
      description: 'Create and manage personalized content'
    },
    {
      path: '/admin/reports',
      icon: FaChartBar,
      label: 'Reports',
      description: 'View analytics and insights'
    },
    ...(isSuperAdmin ? [{
      path: '/admin/users',
      icon: FaUsers,
      label: 'User Management',
      description: 'Manage system users and permissions'
    }] : [])
  ];

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
        <div className="flex items-center justify-between h-14 px-4">
          <button 
            className="btn btn-ghost btn-icon"
            onClick={() => setSidebarOpen(true)}
          >
            <FaBars className="h-4 w-4" />
          </button>
          <div className="flex items-center space-x-3">
            <h1 className="text-sm font-semibold">{getCurrentPageTitle()}</h1>
            <div className="flex items-center space-x-2">
              <div className="h-6 w-6 bg-primary rounded-full flex items-center justify-center">
                {isSuperAdmin ? <FaCrown className="h-3 w-3 text-primary-foreground" /> : <FaUserShield className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="text-xs font-medium">{user.name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Overlay for mobile */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-background border-r border-border transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between h-14 px-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
              <FaCrown className="h-4 w-4 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">Admin Portal</h2>
              <p className="text-xs text-muted-foreground">
                {isSuperAdmin ? 'Super Admin' : 'Admin'}
              </p>
            </div>
          </div>
          <button 
            className="btn btn-ghost btn-icon lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        <div className="mx-4 my-4">
          <div className="card">
            <div className="card-content flex justify-center items-center">
              <div className="flex items-center space-x-3">
                {/* <div className="h-10 w-10 bg-primary rounded-full flex items-center justify-center">
                  {isSuperAdmin ? <FaCrown className="h-5 w-5 text-primary-foreground" /> : <FaUserShield className="h-5 w-5 text-primary-foreground" />}
                </div> */}
                <div className="space-y-1">
                  <h3 className="text-sm font-medium">{user.name}</h3>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  <span className="badge badge-secondary text-xs">
                    {isSuperAdmin ? 'Super Admin' : 'Administrator'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-4">
          <div className="space-y-1">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 py-2">Management</h4>
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive 
                      ? 'primary-indel text-primary-foreground' 
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <IconComponent className="h-4 w-4" />
                  <div className="space-y-0.5">
                    <div className="font-medium">{item.label}</div>
                    <div className="text-xs opacity-80">{item.description}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-border flex item-center">
          <div className="flex space-x-2">
            {/* <button className="btn btn-ghost btn-sm flex-1" onClick={toggleTheme} title="Toggle Theme">
              {theme === 'light' ? <FaMoon className="h-3 w-3 mr-2" /> : <FaSun className="h-3 w-3 mr-2" />}
              <span className="text-xs">Theme</span>
            </button> */}
            
            <Link to="/" className="btn btn-ghost btn-sm flex-1" title="Back to Calendar">
              <FaHome className="h-3 w-3 mr-2" />
              <span className="text-xs">Calendar</span>
            </Link>
          </div>
          
          <button className="btn btn-outline w-full" onClick={logout}>
            <FaSignOutAlt className="h-3 w-3 mr-2" />
            <span className="text-xs">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:pl-0 pt-14 lg:pt-0">
        <div className="h-full px-4 py-6 lg:px-8">
          <Routes>
            <Route path="/" element={<Navigate to="/admin/festivals" />} />
            <Route path="/festivals" element={<FestivalManagement />} />
            <Route path="/special-days" element={<SpecialDaysManagement />} />
            <Route path="/events" element={<WishesManagement />} />
            <Route path="/reports" element={<Reports />} />
            {isSuperAdmin && <Route path="/users" element={<UserManagement />} />}
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
